import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { KycCertificationChoiceMode } from '@dealstack/shared/lib/generated';
import type { CertificationChoiceFormValues } from './CertifiableProof.types';
import { toCertificationChoice } from './CertifiableProof.utils';

const MAX_CERTIFIER_NAME_LENGTH = 200;

/** The investor's certification choice for one proof, validated as they fill it in. */
export const useCertificationChoiceForm = (defaultValues: CertificationChoiceFormValues) => {
	const tInvestor = useTranslationInvestor();

	const schema = z.discriminatedUnion('mode', [
		z.object({
			mode: z.literal(KycCertificationChoiceMode.SELF_ATTESTED),
			attested: z.literal(true, {
				errorMap: () => ({
					message: tInvestor('investment.kyc.certification.attested_required', {
						defaultValue: 'Confirm that this copy has already been certified',
					}),
				}),
			}),
			certifierName: z.string(),
			certifierEmail: z.string(),
		}),
		z.object({
			mode: z.literal(KycCertificationChoiceMode.CERTIFIER),
			attested: z.boolean(),
			certifierName: z
				.string()
				.trim()
				.min(1, {
					message: tInvestor('investment.kyc.certification.certifier_name_required', {
						defaultValue: "Enter the certifier's name",
					}),
				})
				.max(MAX_CERTIFIER_NAME_LENGTH),
			certifierEmail: z
				.string()
				.trim()
				.email({
					message: tInvestor('investment.kyc.certification.certifier_email_invalid', {
						defaultValue: "Enter the certifier's email address",
					}),
				}),
		}),
	]);

	const form = useForm<CertificationChoiceFormValues>({
		mode: 'all',
		resolver: zodResolver(schema),
		defaultValues,
	});
	const values = useWatch({ control: form.control });
	const parsed = schema.safeParse(values);

	return {
		form,
		mode: values.mode ?? defaultValues.mode,
		choice: parsed.success ? toCertificationChoice(parsed.data) : undefined,
	};
};
